import * as React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useCreateStocktake, useStocktakeFilterOptions } from '@/hooks/useStocktakes'
import { ApiError } from '@/lib/api/rawFetch'

const selectCls =
  'h-8 w-full border border-[var(--line-input)] bg-[var(--surface-card)] px-2 text-xs text-[var(--text-primary)] disabled:text-[var(--mute)] focus:outline-none focus:border-[var(--brand)]'
const inputCls = 'h-8 text-xs'

// Location is almost always the store — Stocky defaults it, so we do too.
const DEFAULT_LOCATION_NAME = 'brewster store'

const schema = z.object({
  name: z.string().trim().min(1, 'Give this stocktake a name'),
  location_id: z.string().min(1, 'Pick a location'),
  // A stocktake is always one vendor's rack: the zero-out semantics only hold on
  // a fully enumerated scope, so there is no "all vendors" option.
  vendor: z.string().min(1, 'Pick a vendor'),
  // Empty = every type this vendor sells; otherwise the rack is narrowed to the
  // ticked types, which is how one person counts the shirt wall on its own.
  product_types: z.array(z.string()),
})

type NewStocktakeValues = z.infer<typeof schema>

const EMPTY: NewStocktakeValues = {
  name: '',
  location_id: '',
  vendor: '',
  product_types: [],
}

/** Trigger-line summary: nothing ticked is the "everything" case, and a short
 *  pick reads better spelled out than counted. */
function typeSummary(selected: string[]): string {
  if (selected.length === 0) return 'All types'
  const joined = selected.join(', ')
  return joined.length <= 40 ? joined : `${selected.length} types`
}

export interface NewStocktakeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NewStocktakeDialog({ open, onOpenChange }: NewStocktakeDialogProps) {
  const navigate = useNavigate()
  const create = useCreateStocktake()
  const busy = create.isPending

  const form = useForm<NewStocktakeValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  })
  const vendor = form.watch('vendor')

  // Vendors + locations are vendor-independent, so they come from the unfiltered
  // call and stay put; the product-type list is re-fetched narrowed to the
  // chosen vendor (offering Show Coats under a hat brand is a dead filter).
  const { data: options } = useStocktakeFilterOptions()
  const { data: vendorOptions } = useStocktakeFilterOptions(vendor || undefined)
  const productTypes = vendor
    ? (vendorOptions?.product_types ?? [])
    : (options?.product_types ?? [])

  React.useEffect(() => {
    if (!open) return
    form.reset(EMPTY)
    create.reset()
    // Reset on open only — the mutation object identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Default the location once the list arrives, without stomping a choice the
  // operator already made.
  const locations = options?.locations ?? []
  React.useEffect(() => {
    if (!open || locations.length === 0) return
    if (form.getValues('location_id')) return
    const preferred =
      locations.find((l) => l.name.trim().toLowerCase() === DEFAULT_LOCATION_NAME) ??
      locations[0]
    form.setValue('location_id', String(preferred.location_id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locations])

  async function onSubmit(values: NewStocktakeValues) {
    try {
      const detail = await create.mutateAsync({
        name: values.name.trim(),
        vendor: values.vendor,
        location_id: Number(values.location_id),
        // Omitted rather than sent empty: "All types" means don't narrow, while
        // an empty list would read as "narrow to nothing".
        product_types: values.product_types.length ? values.product_types : undefined,
      })
      onOpenChange(false)
      navigate(`/stocktakes/${detail.session_id}`)
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        // Creation takes a LIVE Shopify read for the Available baseline, so
        // without credentials there is no stocktake to start.
        toast.error('Shopify is not configured — a stocktake needs a live connection')
        return
      }
      toast.error(err instanceof Error ? err.message : 'Failed to create the stocktake')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent
        className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] sm:max-w-[520px]"
        data-testid="new-stocktake-dialog"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">Create Stocktake</DialogTitle>
          <DialogDescription className="text-xs text-[var(--mute)]">
            Every active variant from this vendor at this location is pulled from
            Shopify with its current on-hand quantity as the Available baseline.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
            className="flex flex-col gap-3"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel className="text-xs">Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoFocus
                      disabled={busy}
                      className={inputCls}
                      placeholder="e.g. Dada Sport Breeches — August 2026"
                      data-testid="stocktake-name"
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="location_id"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel className="text-xs">Stocktake Location</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      disabled={busy}
                      className={selectCls}
                      data-testid="stocktake-location"
                    >
                      <option value="">— select —</option>
                      {locations.map((l) => (
                        <option key={l.location_id} value={String(l.location_id)}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="vendor"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel className="text-xs">
                    Vendor or Supplier Stocktake?
                  </FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      disabled={busy}
                      className={selectCls}
                      data-testid="stocktake-vendor"
                      onChange={(e) => {
                        field.onChange(e)
                        // The old types may not exist under the new vendor.
                        form.setValue('product_types', [])
                      }}
                    >
                      <option value="">— select a vendor —</option>
                      {(options?.vendors ?? []).map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {/* Multi-select: a count often covers two neighbouring racks (Show
                Shirts + Training Shirts), which one dropdown value can't say. */}
            <FormField
              control={form.control}
              name="product_types"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel className="text-xs">
                    Limit Number of Product Variants
                  </FormLabel>
                  <FormControl>
                    <div
                      className="border border-[var(--line-warm)] bg-[var(--surface-card)]"
                      data-testid="stocktake-product-types"
                    >
                      <div className="flex items-center justify-between border-b border-[var(--line-warm)] px-2 py-1">
                        <span
                          className="text-xs text-[var(--text-primary)]"
                          data-testid="stocktake-product-types-summary"
                        >
                          {typeSummary(field.value)}
                        </span>
                        {field.value.length > 0 && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => field.onChange([])}
                            className="text-[11px] text-[var(--mute)] hover:text-[var(--text-primary)]"
                            data-testid="stocktake-product-types-clear"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="max-h-[112px] overflow-auto px-2 py-1">
                        {productTypes.length === 0 ? (
                          <p className="py-0.5 text-[11px] italic text-[var(--mute)]">
                            No product types to narrow by.
                          </p>
                        ) : (
                          productTypes.map((t) => (
                            <label
                              key={t}
                              className="flex items-center gap-1.5 py-0.5 text-xs text-[var(--ink-2)]"
                            >
                              <input
                                type="checkbox"
                                disabled={busy}
                                checked={field.value.includes(t)}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.checked
                                      ? [...field.value, t]
                                      : field.value.filter((v: string) => v !== t)
                                  )
                                }
                                data-testid={`stocktake-product-type-${t}`}
                              />
                              {t}
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={busy}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="h-7 text-xs"
                disabled={busy}
                data-testid="stocktake-submit"
              >
                {busy ? 'Creating…' : 'Create Stocktake'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
